import React, { useEffect, useRef, useState } from 'react';
import { Smartphone, Upload, RotateCcw, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../context/AuthContext';

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function describeError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  let body = '';
  if (typeof detail === 'string') body = detail;
  else if (Array.isArray(detail)) body = detail.map((x) => x.msg || JSON.stringify(x)).join('; ');
  else if (detail && typeof detail === 'object') body = detail.message || detail.error || JSON.stringify(detail);
  else if (e?.message) body = e.message;
  return status ? `HTTP ${status} — ${body || 'error'}` : (body || 'Network error');
}

/**
 * Compact APK-release card for the CRM admin dashboard.
 * Shows current version/build/upload time + a re-upload dialog + rollback.
 * Backed by GET/POST /api/admin/apk[/upload|/rollback].
 */
export default function ApkReleaseCard() {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { pct: 0..100, sent, total }
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    latest_version: '', latest_build: '', min_build: '1', notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/apk');
      setMeta(data);
      setForm((f) => ({
        ...f,
        latest_version: data?.latest_version || f.latest_version,
        latest_build: data?.latest_build ? String(data.latest_build + 1) : f.latest_build,
        min_build: data?.min_build ? String(data.min_build) : f.min_build,
      }));
    } catch (e) {
      toast({ title: 'APK metadata failed to load', description: describeError(e), variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast({ title: 'Pick an APK file first', variant: 'destructive' });
      return;
    }
    setBusy(true);
    setProgress({ pct: 0, sent: 0, total: file.size });
    try {
      // 1) Open a chunked-upload session. Backend picks the chunk size
      //    (currently ~4 MB, well below the ingress cap).
      const { data: init } = await api.post('/admin/apk/upload/init');
      const chunkSize = init.chunk_size || 4 * 1024 * 1024;
      if (file.size > (init.max_size_bytes || 0)) {
        throw new Error(`APK exceeds server limit (${Math.round(init.max_size_bytes / 1024 / 1024)} MB)`);
      }

      // 2) Ship chunks sequentially so the server can enforce
      //    contiguous ordering + cumulative size checks.
      let sent = 0;
      const totalChunks = Math.ceil(file.size / chunkSize);
      for (let i = 0; i < totalChunks; i += 1) {
        const start = i * chunkSize;
        const blob = file.slice(start, Math.min(start + chunkSize, file.size));
        // eslint-disable-next-line no-await-in-loop
        await api.post(
          `/admin/apk/upload/chunk?session_id=${encodeURIComponent(init.session_id)}&index=${i}`,
          blob,
          { headers: { 'Content-Type': 'application/octet-stream' } },
        );
        sent += blob.size;
        setProgress({
          pct: Math.round((sent / file.size) * 100),
          sent, total: file.size,
        });
      }

      // 3) Publish — concat + validate + write manifest.
      const fd = new FormData();
      fd.append('session_id', init.session_id);
      fd.append('latest_version', form.latest_version.trim());
      fd.append('latest_build', String(form.latest_build).trim());
      fd.append('min_build', String(form.min_build).trim());
      fd.append('notes', form.notes.trim());
      fd.append('filename', file.name || 'listing-aigent.apk');
      const { data } = await api.post('/admin/apk/upload/finish', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: `Released v${data.latest_version} (build ${data.latest_build})` });
      setShowForm(false);
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (e) {
      toast({ title: 'Upload failed', description: describeError(e), variant: 'destructive' });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const rollback = async () => {
    if (!window.confirm(
      `Roll back to the previous build? The current v${meta?.latest_version} ` +
      `(build ${meta?.latest_build}) will be pushed onto the history stack.`,
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post('/admin/apk/rollback');
      toast({ title: `Rolled back to v${data.latest_version} (build ${data.latest_build})` });
      await load();
    } catch (e) {
      toast({ title: 'Rollback failed', description: describeError(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const historyLen = meta?.history?.length || 0;

  return (
    <Card className="p-5" data-testid="apk-release-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-brand-cream p-2 mt-0.5">
            <Smartphone className="h-4 w-4 text-brand-green" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-gold">Android · APK release</div>
            {loading ? (
              <div className="mt-1 text-sm text-brand-green/60">Loading…</div>
            ) : (
              <>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-serif text-2xl text-brand-green" data-testid="apk-latest-version">
                    v{meta?.latest_version || '—'}
                  </span>
                  <span className="text-sm text-brand-green/70" data-testid="apk-latest-build">
                    build {meta?.latest_build ?? '—'}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-brand-green/70">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {formatTime(meta?.uploaded_at)}
                  </span>
                  <span>·</span>
                  <span>min_build {meta?.min_build ?? '—'}</span>
                  {meta?.apk_size_bytes ? (
                    <>
                      <span>·</span>
                      <span>{formatBytes(meta.apk_size_bytes)}</span>
                    </>
                  ) : null}
                </div>
                {!meta?.uploaded ? (
                  <div className="mt-1 text-xs text-brand-gold">
                    No APK uploaded yet — download URL returns 503 until the first release.
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => setShowForm((v) => !v)}
            data-testid="apk-toggle-upload"
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            {showForm ? 'Cancel' : 'Re-upload'}
          </Button>
          {historyLen > 0 ? (
            <Button
              size="sm" variant="ghost" disabled={busy}
              onClick={rollback}
              data-testid="apk-rollback-btn"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Rollback ({historyLen})
            </Button>
          ) : null}
        </div>
      </div>

      {showForm ? (
        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-brand-green/10 pt-4">
          <div className="col-span-2">
            <Label htmlFor="apk-file" className="text-xs">APK file (signed, same key as previous releases)</Label>
            <input
              id="apk-file" type="file" accept=".apk,application/vnd.android.package-archive"
              ref={fileRef}
              className="mt-1 block w-full text-sm text-brand-green file:mr-3 file:rounded file:border-0 file:bg-brand-green file:px-3 file:py-1.5 file:text-white file:cursor-pointer"
              data-testid="apk-file-input"
            />
          </div>
          <div>
            <Label htmlFor="apk-version" className="text-xs">Version (semver)</Label>
            <Input
              id="apk-version" value={form.latest_version}
              onChange={(e) => setForm((f) => ({ ...f, latest_version: e.target.value }))}
              placeholder="1.0.1"
              data-testid="apk-version-input"
            />
          </div>
          <div>
            <Label htmlFor="apk-build" className="text-xs">Build (must match versionCode)</Label>
            <Input
              id="apk-build" type="number" min="1" value={form.latest_build}
              onChange={(e) => setForm((f) => ({ ...f, latest_build: e.target.value }))}
              placeholder="2"
              data-testid="apk-build-input"
            />
          </div>
          <div>
            <Label htmlFor="apk-min-build" className="text-xs">Min build (force-update floor)</Label>
            <Input
              id="apk-min-build" type="number" min="1" value={form.min_build}
              onChange={(e) => setForm((f) => ({ ...f, min_build: e.target.value }))}
              placeholder="1"
              data-testid="apk-minbuild-input"
            />
          </div>
          <div>
            <Label htmlFor="apk-notes" className="text-xs">Release notes (shown in-app)</Label>
            <Input
              id="apk-notes" value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Bug fixes and speed improvements."
              data-testid="apk-notes-input"
            />
          </div>
          <div className="col-span-2 flex items-center justify-end gap-3">
            {progress && (
              <div className="flex-1 flex items-center gap-2" data-testid="apk-upload-progress">
                <div className="flex-1 h-2 rounded-full bg-brand-cream overflow-hidden">
                  <div
                    className="h-full bg-brand-green transition-all"
                    style={{ width: `${progress.pct}%` }}
                    data-testid="apk-upload-progress-bar"
                  />
                </div>
                <span className="text-xs text-brand-green/70 tabular-nums w-32 text-right">
                  {formatBytes(progress.sent)} / {formatBytes(progress.total)} · {progress.pct}%
                </span>
              </div>
            )}
            <Button onClick={upload} disabled={busy} data-testid="apk-upload-submit">
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              {busy
                ? (progress ? `Uploading ${progress.pct}%…` : 'Uploading…')
                : 'Publish release'}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
